import { http } from '@/api/httpClient'
import { requestQuery } from '@/api/requestQuery'

const ENTERPRISE = '/api/v1/lab/operational-masters/enterprise-investigations'
const HOSPITAL = '/api/v1/lab/operational-masters/hospital-investigations'

export type LabEnterpriseInvestigation = {
  investigationMasterId: string
  investigationCode: string
  displayName: string
  shortName?: string | null
  category: string
  specimenType?: string | null
  defaultDepartment?: string | null
  defaultTurnaroundMinutes?: number | null
  billableFlag: boolean
  activeFlag: string
  packageEligiblePlaceholder?: boolean
  opVersion?: number | null
  /** Present on create: false for a new row, true when an inactive row was reactivated. */
  reactivated?: boolean
}

export type LabHospitalInvestigationLookup = {
  mappingId: string
  investigationMasterId: string
  investigationCode: string
  displayName: string
  localDisplayName?: string | null
  localCode?: string | null
  category: string
  billingServiceId: string
  tariffAmount?: number | null
  billable: boolean
  enabled: boolean
}

export async function searchLabEnterpriseMasters(q?: string, activeFlag: 'A' | 'I' = 'A') {
  const res = await http.get<LabEnterpriseInvestigation[]>(
    `${ENTERPRISE}/search${requestQuery({ q, activeFlag })}`,
  )
  return res.data ?? []
}

export async function createLabEnterpriseMaster(body: Partial<LabEnterpriseInvestigation>) {
  const res = await http.post<LabEnterpriseInvestigation>(`${ENTERPRISE}/create`, body)
  return res.data
}

export async function modifyLabEnterpriseMaster(body: LabEnterpriseInvestigation) {
  const res = await http.post<LabEnterpriseInvestigation>(`${ENTERPRISE}/modify`, body)
  return res.data
}

export async function setLabEnterpriseActive(masterId: string, active: boolean) {
  const path = active ? `${ENTERPRISE}/${masterId}/activate` : `${ENTERPRISE}/${masterId}/deactivate`
  const res = await http.post<LabEnterpriseInvestigation>(path, {})
  return res.data
}

export async function searchLabHospitalMappings(
  q?: string,
  limit = 80,
  includeDisabled = false,
) {
  const res = await http.get<LabHospitalInvestigationLookup[]>(
    `${HOSPITAL}/search${requestQuery({ q, limit, includeDisabled: includeDisabled || undefined })}`,
  )
  return res.data ?? []
}

export async function resolveLabHospitalTariff(mappingId: string) {
  const res = await http.get<LabHospitalInvestigationLookup>(`${HOSPITAL}/tariff/${mappingId}`)
  return res.data
}

export async function createLabHospitalMapping(body: Record<string, unknown>) {
  const res = await http.post(`${HOSPITAL}/create`, body)
  return res.data
}

export async function modifyLabHospitalMapping(body: Record<string, unknown>) {
  const res = await http.post(`${HOSPITAL}/modify`, body)
  return res.data
}

/** Push hospital mapping tariffs into billing catalog (OP counter price resolution). */
export async function syncLabHospitalTariffsToBilling() {
  const res = await http.post<{ synced: number }>(`${HOSPITAL}/sync-billing-tariffs`, {})
  return res.data
}

export async function setLabHospitalMappingActive(mappingId: string, active: boolean) {
  const path = active ? `${HOSPITAL}/${mappingId}/activate` : `${HOSPITAL}/${mappingId}/deactivate`
  const res = await http.post(path, {})
  return res.data
}
